import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import AdBanner from '../../components/AdBanner';
import { AD_SLOTS } from '../../config/adSlots';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { useStructuredData } from '../../hooks/useStructuredData';
import { useAuthContext } from '../../contexts/AuthContext';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
}

const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'what-is-fantasy-wrestling',
    title: "What Is Fantasy Wrestling? A Complete Beginner's Guide",
    description:
      'Fantasy wrestling brings the excitement of fantasy sports to NCAA wrestling. Learn how it works, how to draft wrestlers, and why wrestling fans are hooked.',
    date: '2026-03-01',
    readTime: '6 min read',
  },
  {
    slug: 'ncaa-wrestling-scoring-explained',
    title: 'NCAA Wrestling Scoring Rules Explained Simply',
    description:
      'A straightforward breakdown of how NCAA wrestling tournament scoring works — placement points, advancement points, bonus points, and how they add up.',
    date: '2026-03-03',
    readTime: '7 min read',
  },
  {
    slug: 'fantasy-wrestling-draft-strategy',
    title: 'Fantasy Wrestling Draft Strategy: Tips to Win Your League',
    description:
      'Proven strategies for building a winning fantasy wrestling roster. Learn when to spend big on top seeds and when mid-seed value picks pay off.',
    date: '2026-03-05',
    readTime: '7 min read',
  },
  {
    slug: 'ncaa-wrestling-tournament-format',
    title: 'How NCAA Wrestling Tournaments Work: Brackets, Rounds & Format Explained',
    description:
      'A clear explanation of how NCAA wrestling tournaments are structured — championship bracket, consolation bracket, pigtails, and the path to All-American status.',
    date: '2026-03-07',
    readTime: '7 min read',
  },
  {
    slug: 'how-to-run-fantasy-wrestling-league',
    title: "How to Run a Fantasy Wrestling League: Commissioner's Guide",
    description:
      'A step-by-step guide for commissioners on creating and managing a fantasy wrestling league — from setup to draft to crowning a champion.',
    date: '2026-03-10',
    readTime: '6 min read',
  },
];

export { BLOG_POSTS };

export default function BlogListingPage() {
  const { isAuthenticated } = useAuthContext();

  useDocumentMeta(
    'Blog',
    'Articles, guides, and strategy tips for fantasy wrestling and NCAA wrestling tournaments. Learn how to draft, score, and win your Takedown League.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Takedown League Blog',
    description:
      'Articles, guides, and strategy tips for fantasy wrestling and NCAA wrestling tournaments.',
    url: 'https://takedownleague.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'Takedown League',
      url: 'https://takedownleague.com',
    },
  });

  return (
    <PublicLayout>
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Blog</h1>
          <p className="text-xl text-purple-100">
            Guides, strategy tips, and everything you need to know about fantasy wrestling.
          </p>
        </div>
      </div>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-8">
            {BLOG_POSTS.map((post, index) => (
              <div key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="block group border border-gray-200 rounded-lg p-6 hover:border-purple-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                    <time dateTime={post.date}>
                      {new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </time>
                    <span>|</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors mb-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{post.description}</p>
                  <span className="inline-block mt-3 text-purple-600 font-medium text-sm group-hover:text-purple-700">
                    Read more &rarr;
                  </span>
                </Link>
                {index === 1 && (
                  <div className="mt-8">
                    <AdBanner slot={AD_SLOTS.blog} format="horizontal" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {isAuthenticated ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Compete?</h2>
              <p className="text-purple-100 mb-6">
                Head back to your leagues and put these strategies to work.
              </p>
              <Link
                to="/leagues"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Back to My Leagues
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Play?</h2>
              <p className="text-purple-100 mb-6">
                Create your free account and start drafting your wrestling roster today.
              </p>
              <Link
                to="/login?tab=register"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Create Your Free Account
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
